<template>
  <div class="model-management-page">
    <!-- 实名认证拦截弹窗 -->
    <div class="auth-modal" v-if="showAuthModal">
      <div class="auth-modal-content">
        <h3 class="auth-modal-title">实名认证提醒</h3>
        <p class="auth-modal-desc">
          您尚未完成实名认证，无法进行模型管理相关操作。<br>
          请先完善实名认证信息，完成后即可正常管理模型。<br>
          如有疑问，欢迎随时联系我们。
        </p>
        <button class="auth-modal-btn" @click="goToAuthPage">去完善</button>
      </div>
    </div>

    <!-- 下架确认弹窗 -->
    <div class="confirm-modal" v-if="showRemoveConfirm">
      <div class="confirm-modal-content">
        <h3 class="confirm-modal-title">确认下架</h3>
        <p class="confirm-modal-desc">
          警告：下架的模型将无法恢复，请谨慎操作！<br>
          您确定要下架该模型吗？
        </p>
        <div class="confirm-modal-buttons">
          <button class="modal-btn cancel-btn" @click="cancelRemove">取消</button>
          <button class="modal-btn confirm-btn" @click="confirmRemove" :disabled="removeLoading">
            {{ removeLoading ? '处理中...' : '确认下架' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 选项卡导航 -->
    <div class="tab-nav" v-if="isAuthenticated">
      <button class="tab-btn" :class="{ active: activeTab === 'published' }"
        @click="handleTabChange('published')">已上架</button>
      <button class="tab-btn" :class="{ active: activeTab === 'pending' }"
        @click="handleTabChange('pending')">待审核</button>
      <!-- <button class="tab-btn" :class="{ active: activeTab === 'draft' }" @click="handleTabChange('draft')">草稿箱</button> -->
      <button class="tab-btn" :class="{ active: activeTab === 'rejected' }"
        @click="handleTabChange('rejected')">未通过</button>
      <button class="tab-btn" :class="{ active: activeTab === 'sold' }" @click="handleTabChange('sold')">已售出</button>
    </div>

    <!-- 审核提示条 -->
    <div class="notice-bar" v-if="activeTab === 'pending' && isAuthenticated">
      注意: 管理员会在发帖之后1-3个工作日内进行审核，请耐心等待。
    </div>

    <!-- 筛选区域 -->
    <div class="filter-area" v-if="isAuthenticated">
      <div class="filter-item">
        <label class="filter-label">模型标题:</label>
        <input type="text" class="filter-input" placeholder="搜索模型标题" v-model="searchForm.title">
      </div>
      <div class="filter-item">
        <label class="filter-label">发布时间:</label>
        <input type="date" class="filter-input" placeholder="开始时间" v-model="searchForm.start">
        <span class="filter-separator">至</span>
        <input type="date" class="filter-input" placeholder="结束时间" v-model="searchForm.end">
      </div>
      <div class="filter-buttons">
        <button class="filter-btn" @click="handleSearch">查询</button>
        <button class="filter-btn" @click="handleReset">重置</button>
      </div>

      <!-- 批量操作区域 仅已上架显示 -->
      <div class="batch-operation" v-if="activeTab === 'published'">
        <button class="batch-btn batch-select-all" @click="selectAllModels">
          {{ isAllSelected ? '取消全选' : '全选' }}
        </button>
        <button class="batch-btn batch-price-btn" @click="openPriceModal" :disabled="selectedModelIds.length === 0">
          批量改价
        </button>
      </div>
    </div>

    <!-- 模型列表（增加横向滚动容器） -->
    <div class="model-list" v-if="isAuthenticated">
      <!-- 横向滚动容器，底部出现滚动条 -->
      <div class="table-scroll-wrap">
        <table class="model-table">
          <thead>
            <tr>
              <!-- 复选框仅已上架显示 -->
              <th v-if="activeTab === 'published'" class="checkbox-column">
                <input type="checkbox" v-model="isAllSelected" @change="handleAllSelectChange"
                  class="select-all-checkbox">
              </th>
              <th>模型预览图</th>
              <th>模型ID</th>
              <th>模型名称</th>
              <th>标价(云币)</th>
              <!-- 已上架专属列 -->
              <th v-if="activeTab === 'published'">销量</th>
              <th v-if="activeTab === 'published'">点击量</th>
              <th v-if="activeTab === 'published'">指导价</th>
              <!-- 已售出专属列 -->
              <th v-if="activeTab === 'sold'">买家ID</th>
              <th v-if="activeTab === 'sold'">买家昵称</th>
              <th v-if="activeTab === 'sold'">实付金额</th>
              <!-- 共用时间列 -->
              <th>发布时间</th>
              <th v-if="activeTab === 'sold'">成交时间</th>
              <th>授权方式</th>
              <!-- 已上架操作列 -->
              <th v-if="activeTab === 'published'">价格操作</th>
              <th v-if="activeTab === 'published'">操作</th>
            </tr>
          </thead>
          <tbody>
            <!-- 加载中提示 -->
            <tr v-if="loading">
              <!-- 动态计算合并列数 -->
              <td :colspan="getTableColSpan()" style="text-align: center; padding: 20px;">
                加载中...
              </td>
            </tr>
            <!-- 无数据提示 -->
            <tr v-else-if="total === 0">
              <td :colspan="getTableColSpan()" style="text-align: center; padding: 20px;">
                暂无相关模型数据
              </td>
            </tr>
            <!-- 数据列表 -->
            <tr v-else v-for="item in modelList" :key="item.modelId">
              <!-- 复选框单元格 -->
              <td v-if="activeTab === 'published'" class="checkbox-cell">
                <input type="checkbox" :value="item.modelId" v-model="selectedModelIds" class="model-checkbox">
              </td>
              <!-- 预览图列 -->
              <td class="model-thumbnail">
                <img :src="item.coverUrl || 'https://via.placeholder.com/80x60?text=暂无图片'" :alt="item.title"
                  class="model-img" @click="goToDetail(item.modelId)">
              </td>
              <!-- 模型ID -->
              <td>{{ item.modelId }}</td>
              <!-- 名称列 -->
              <td class="model-title-cell">
                <span class="model-title" @click="goToDetail(item.modelId)">{{ item.title }}</span>
              </td>
              <!-- 当前价格列 -->
              <td class="price">{{ item.licensingPrices }}云币</td>
              <!-- 已上架独有 -->
              <td v-if="activeTab === 'published'">{{ item.salesVolume }}</td>
              <td v-if="activeTab === 'published'">{{ item.clickThrough }}</td>
              <td class="guide-price" v-if="activeTab === 'published'">
                {{ item.changePrice ? `${item.changePrice}云币` : '无' }}
              </td>
              <!-- 已售出独有字段 -->
              <td v-if="activeTab === 'sold'">{{ item.buyerid }}</td>
              <td v-if="activeTab === 'sold'">{{ item.userName }}</td>
              <td v-if="activeTab === 'sold'" class="price">{{ item.amountPaid }}云币</td>
              <!-- 发布时间 -->
              <td>{{ item.creationTime }}</td>
              <!-- 成交时间 仅售出 -->
              <td v-if="activeTab === 'sold'">{{ item.paymentTime }}</td>
              <!-- 授权方式列 -->
              <td>{{ item.releaseModel }}</td>
              <!-- 价格操作列 -->
              <td v-if="activeTab === 'published'">
                <button class="operate-btn apply-price-btn" @click.stop="applyGuidePrice(item.modelId)"
                  :disabled="item.isApplying" v-if="item.changePrice != ''">
                  {{ item.isApplying ? '应用中...' : '应用指导价' }}
                </button>
                <span v-else>无</span>
              </td>
              <!-- 操作列 -->
              <td v-if="activeTab === 'published'">
                <button class="operate-btn" @click.stop="openRemoveConfirm(item.modelId)">下架</button>
                <button class="operate-btn" @click.stop="ModelExit(item.modelId)">修改</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 分页组件 -->
    <div class="pagination-container" v-if="total > 0 && isAuthenticated">
      <el-pagination @current-change="handleCurrentChange" :current-page="pageIndex" :page-size="pageSize"
        layout="total, prev, pager, next, jumper" :total="total" background>
      </el-pagination>
    </div>

    <!-- 未认证提示 -->
    <div class="unauth-tip" v-if="!isAuthenticated">
      <div class="tip-content">
        <i class="tip-icon"></i>
        <p>请先完成实名认证，才能进行模型管理相关操作</p>
        <button class="tip-btn" @click="goToAuthPage">去实名认证</button>
      </div>
    </div>

    <!-- 批量改价弹窗 -->
    <div class="price-modal" v-if="showPriceModal">
      <div class="price-modal-content">
        <div class="price-modal-header">
          <h3 class="price-modal-title">批量修改模型价格</h3>
          <span class="price-modal-close" @click="closePriceModal">×</span>
        </div>
        <div class="price-modal-body">
          <div class="price-input-group">
            <label class="price-label">新价格（云币）:</label>
            <input type="number" class="price-input" v-model="newModelPrice" placeholder="请输入新价格" min="0" step="0.01">
          </div>
          <div class="selected-count">
            已选择 {{ selectedModelIds.length }} 个模型
          </div>
        </div>
        <div class="price-modal-footer">
          <button class="modal-btn cancel-btn" @click="closePriceModal">取消</button>
          <button class="modal-btn confirm-btn" @click="confirmBatchPriceChange"
            :disabled="!newModelPrice || newModelPrice < 0 || batchPriceLoading">
            {{ batchPriceLoading ? '提交中...' : '确认修改' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ModelManagementPage',
  data() {
    return {
      activeTab: 'published',
      searchForm: {
        title: "",
        start: "",
        end: ""
      },
      modelList: [],
      loading: false,
      isAuthenticated: null,
      showAuthModal: null,
      pageIndex: 1,
      pageSize: 10,
      total: 0,
      selectedModelIds: [],
      isAllSelected: false,
      showPriceModal: false,
      newModelPrice: '',
      batchPriceLoading: false,
      showRemoveConfirm: false,
      currentRemoveModelId: '',
      removeLoading: false
    }
  },
  async mounted() {
    await this.checkAuthStatus();
    const { page, tab } = this.$route.query;
    if (page) this.pageIndex = Number(page);
    if (tab) this.activeTab = tab;
    if (this.isAuthenticated === true) {
      this.getModelManagement();
    }
  },
  watch: {
    modelList() {
      this.updateAllSelectStatus();
    },
    selectedModelIds() {
      this.updateAllSelectStatus();
    }
  },
  methods: {
    // 动态计算表格合并列数（适配已上架/已售出不同列）
    getTableColSpan() {
      if (this.activeTab === 'published') {
        // 已上架：复选框+预览图+模型ID+名称+标价+销量+点击+指导价+发布时间+授权+价格操作+操作 = 12
        return 12;
      } else if (this.activeTab === 'sold') {
        // 已售出：预览图+模型ID+名称+标价+买家ID+买家昵称+实付+发布时间+成交时间+授权 = 10
        return 10;
      } else {
        // 待审核/未通过：预览图+模型ID+名称+标价+发布时间+授权 =6
        return 6;
      }
    },

    updateAllSelectStatus() {
      if (this.modelList.length === 0) {
        this.isAllSelected = false;
        return;
      }
      const allModelIds = this.modelList.map(item => item.modelId);
      this.isAllSelected = allModelIds.every(id => this.selectedModelIds.includes(id));
    },

    handleAllSelectChange() {
      if (this.isAllSelected) {
        this.selectedModelIds = this.modelList.map(item => item.modelId);
      } else {
        this.selectedModelIds = [];
      }
    },

    selectAllModels() {
      this.isAllSelected = !this.isAllSelected;
      this.handleAllSelectChange();
    },

    openPriceModal() {
      if (!this.isAuthenticated) {
        this.$message?.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }
      if (this.selectedModelIds.length === 0) {
        this.$message?.warning('请先选择需要修改价格的模型');
        return;
      }
      this.newModelPrice = '';
      this.showPriceModal = true;
    },

    closePriceModal() {
      this.showPriceModal = false;
      this.newModelPrice = '';
    },

    async confirmBatchPriceChange() {
      if (!this.newModelPrice || this.newModelPrice < 0) {
        this.$message?.error('请输入有效的价格（大于等于0）');
        return;
      }
      this.batchPriceLoading = true;
      try {
        const res = await this.$apiFun.postUpModelPrice({
          modelId: this.selectedModelIds,
          modelPrice: Number(this.newModelPrice)
        });
        if (res.status === 200 || res.code === 200) {
          this.$message?.success('批量修改价格成功');
          this.closePriceModal();
          this.selectedModelIds = [];
          this.getModelManagement();
        } else {
          this.$message?.error('批量修改价格失败：' + (res.msg || '服务器错误'));
        }
      } catch (err) {
        console.error('批量修改价格失败:', err);
        this.$message?.error('批量修改价格失败，请稍后重试');
      } finally {
        this.batchPriceLoading = false;
      }
    },

    openRemoveConfirm(modelId) {
      if (!this.isAuthenticated) {
        this.$message?.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }
      this.currentRemoveModelId = modelId;
      this.showRemoveConfirm = true;
    },

    cancelRemove() {
      this.showRemoveConfirm = false;
      this.currentRemoveModelId = '';
    },

    async confirmRemove() {
      if (!this.currentRemoveModelId) {
        this.$message?.error('模型ID不存在');
        this.showRemoveConfirm = false;
        return;
      }
      this.removeLoading = true;
      try {
        const res = await this.$apiFun.getModelRemove({ modelId: this.currentRemoveModelId });
        if (res.status === 200 || res.code === 200) {
          this.getModelManagement();
          this.$message?.success('模型下架成功');
          this.total = Math.max(0, this.total - 1);
          if (this.modelList.length === 1 && this.pageIndex > 1) {
            this.pageIndex -= 1;
            this.getModelManagement();
          }
          this.selectedModelIds = this.selectedModelIds.filter(modelId => modelId !== this.currentRemoveModelId);
        }
      } catch (err) {
        console.error('模型下架失败:', err);
        if (this.$message) {
          this.$message.error('模型下架失败，请稍后重试');
        } else {
          alert('模型下架失败，请稍后重试');
        }
      } finally {
        this.removeLoading = false;
        this.showRemoveConfirm = false;
        this.currentRemoveModelId = '';
      }
    },

    async checkAuthStatus() {
      try {
        const res = await this.$apiFun.getSeleMerchantCA();
        if (res.status === 200 || res.code === 200) {
          this.isAuthenticated = !!res.obj?.merchantCA;
          this.showAuthModal = !this.isAuthenticated;
        } else {
          this.$message?.error("认证状态查询失败");
          this.isAuthenticated = false;
          this.showAuthModal = true;
        }
      } catch (err) {
        this.$message?.error('认证状态查询失败');
        console.error('查询认证状态出错：', err);
        this.isAuthenticated = false;
        this.showAuthModal = true;
      }
    },

    goToAuthPage() {
      this.showAuthModal = false;
      this.$router?.push('/dashboard/seller/info');
    },

    async ModelRemove(id) {
      this.currentRemoveModelId = id;
      await this.confirmRemove();
    },

    ModelExit(id) {
      const routeInfo = this.$router.resolve({
        path: '/exitModel',
        query: {
          id: id,
          fromPage: this.pageIndex,
          fromTab: this.activeTab
        }
      });
      window.open(routeInfo.href, '_blank');
    },

    async applyGuidePrice(modelId) {
      if (!this.isAuthenticated) {
        this.$message?.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }
      const modelIndex = this.modelList.findIndex(item => item.modelId === modelId);
      if (modelIndex === -1) return;
      this.modelList[modelIndex].isApplying = true;
      try {
        const res = await this.$apiFun.getModelAgree({
          modelId: modelId,
          whetherAgree: true
        });
        if (res.status === 200 || res.code === 200) {
          this.$message?.success('指导价应用成功');
          this.getModelManagement();
        } else {
          this.$message?.error('指导价应用失败，请稍后重试');
        }
      } catch (err) {
        console.error('应用指导价失败:', err);
        this.$message?.error('指导价应用失败，请稍后重试');
      } finally {
        this.modelList[modelIndex].isApplying = false;
      }
    },

    goToDetail(modelId) {
      if (!this.isAuthenticated) {
        this.$message?.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }
      if (!modelId) return;
      const routeInfo = this.$router?.resolve({
        path: '/detail',
        query: { id: modelId }
      });
      if (routeInfo) {
        window.open(routeInfo.href, '_blank');
      }
    },

    handleTabChange(tabType) {
      if (!this.isAuthenticated) {
        this.$message?.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }
      this.activeTab = tabType;
      this.pageIndex = 1;
      this.$router.replace({ query: {} });
      this.selectedModelIds = [];
      this.isAllSelected = false;
      this.getModelManagement();
    },

    handleSearch() {
      if (!this.isAuthenticated) {
        this.$message?.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }
      this.pageIndex = 1;
      this.$router.replace({ query: {} });
      this.selectedModelIds = [];
      this.isAllSelected = false;
      this.getModelManagement();
    },

    handleReset() {
      if (!this.isAuthenticated) {
        this.$message?.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }
      this.searchForm = { title: "", start: "", end: "" };
      this.pageIndex = 1;
      this.$router.replace({ query: {} });
      this.selectedModelIds = [];
      this.isAllSelected = false;
      this.getModelManagement();
    },

    async getModelManagement() {
      this.loading = true;
      if (!this.isAuthenticated) {
        this.loading = false;
        return;
      }
      const tabStatusMap = {
        'published': '已上架',
        'pending': '待审核',
        'draft': '草稿箱',
        'rejected': '未通过',
        'sold': '已售出'
      };
      const requestParams = {
        modelStatus: tabStatusMap[this.activeTab],
        title: this.searchForm.title.trim(),
        start: this.searchForm.start,
        end: this.searchForm.end,
        pageIndex: this.pageIndex,
        pageSize: this.pageSize
      };
      console.log('请求参数:', requestParams);
      try {
        const res = await this.$apiFun.getModelManagement(requestParams);
        if (res.status === 200 || res.code === 200) {
          // 接口返回 obj 数组、num 总条数，完全匹配你提供的返回结构
          this.modelList = res.obj || [];
          this.total = res.num || 0;
          console.log('模型列表加载成功:', this.modelList, '总条数:', this.total);
        } else {
          this.modelList = [];
          this.total = 0;
          this.$message?.error('模型列表加载失败，请稍后重试');
        }
      } catch (err) {
        console.error('模型列表加载失败:', err);
        this.modelList = [];
        this.total = 0;
        if (this.$message) {
          this.$message.error('模型列表加载失败，请稍后重试');
        } else {
          alert('模型列表加载失败，请稍后重试');
        }
      } finally {
        this.loading = false;
      }
    },

    handleCurrentChange(val) {
      this.pageIndex = val;
      this.$router.replace({
        query: {
          ...this.$route.query,
          page: this.pageIndex,
          tab: this.activeTab
        }
      });
      this.selectedModelIds = [];
      this.isAllSelected = false;
      this.getModelManagement();
    }
  },
}
</script>

<style lang="less" scoped>
// 深色主题变量
@bg-primary: #1e1e1e;
@bg-secondary: #2c2c2c;
@text-primary: #e0e0e0;
@text-secondary: #a0a0a0;
@accent-blue: #0ea5e9;
@border-color: #444;
@input-bg: #333;
@hover-bg: #333333;

.model-management-page {
  color: @text-primary;
  min-height: 100vh;
  padding: 20px;
  box-sizing: border-box;
  position: relative;

  // 横向滚动容器（新增）
  .table-scroll-wrap {
    width: 100%;
    overflow-x: auto;
    // 底部滚动条样式美化
    &::-webkit-scrollbar {
      height: 8px;
    }
    &::-webkit-scrollbar-track {
      background: #222;
    }
    &::-webkit-scrollbar-thumb {
      background: #555;
      border-radius: 4px;
      &:hover {
        background: #777;
      }
    }
  }

  // 实名认证弹窗样式
  .auth-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;

    .auth-modal-content {
      background-color: @bg-secondary;
      border-radius: 8px;
      width: 400px;
      padding: 30px 20px;
      text-align: center;

      .auth-modal-title {
        font-size: 20px;
        color: @text-primary;
        margin-bottom: 15px;
        font-weight: 500;
      }

      .auth-modal-desc {
        font-size: 14px;
        color: @text-secondary;
        line-height: 1.8;
        margin-bottom: 20px;
      }

      .auth-modal-btn {
        background-color: #0ea5e9;
        color: #fff;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        font-size: 16px;
        cursor: pointer;
        width: 100%;
        max-width: 200px;
      }
    }
  }

  // 下架确认弹窗样式
  .confirm-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1002;

    .confirm-modal-content {
      background-color: @bg-secondary;
      border-radius: 8px;
      width: 400px;
      max-width: 90%;
      padding: 20px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);

      .confirm-modal-title {
        font-size: 18px;
        color: @text-primary;
        margin: 0 0 15px 0;
        font-weight: 500;
        color: #ef4444; // 警告色
      }

      .confirm-modal-desc {
        font-size: 14px;
        color: @text-secondary;
        line-height: 1.8;
        margin-bottom: 20px;
      }

      .confirm-modal-buttons {
        display: flex;
        justify-content: flex-end;
        gap: 10px;

        .modal-btn {
          padding: 8px 20px;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;

          &.cancel-btn {
            background-color: @bg-secondary;
            color: @text-primary;
            border: 1px solid @border-color;

            &:hover {
              border-color: @accent-blue;
              color: @accent-blue;
            }
          }

          &.confirm-btn {
            background-color: #ef4444; // 危险色
            color: #fff;
            border: 1px solid #ef4444;

            &:hover:not(:disabled) {
              background-color: #dc2626;
              border-color: #dc2626;
            }

            &:disabled {
              background-color: #4a4a4a;
              border-color: #4a4a4a;
              color: #999;
              cursor: not-allowed;
              opacity: 0.8;
            }
          }
        }
      }
    }
  }

  // 未认证提示样式
  .unauth-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background-color: @bg-secondary;
    border-radius: 6px;
    padding: 20px;
    margin-top: 20px;

    .tip-content {
      text-align: center;

      .tip-icon {
        display: inline-block;
        width: 60px;
        height: 60px;
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><path fill="%230ea5e9" d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"/><path fill="%230ea5e9" d="M512 140c-205.4 0-372 166.6-372 372s166.6 372 372 372 372-125.4 372-372-125.4-280-280-280zm0 508c-125.9 0-228-102.1-228-228s102.1-228 228-228 228 102.1 228 228-102.1 228-228 228z"/></svg>') no-repeat center;
        margin-bottom: 20px;
      }

      p {
        font-size: 16px;
        color: @text-secondary;
        margin-bottom: 20px;
      }

      .tip-btn {
        background-color: @accent-blue;
        color: #fff;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        font-size: 14px;
        cursor: pointer;
      }
    }
  }

  // 选项卡导航
  .tab-nav {
    display: flex;
    gap: 1px;
    margin-bottom: 15px;

    .tab-btn {
      background-color: @bg-secondary;
      border: none;
      padding: 10px 20px;
      font-size: 14px;
      color: @text-secondary;
      cursor: pointer;
      transition: all 0.2s;

      &.active {
        background-color: @bg-primary;
        color: @accent-blue;
        border-bottom: 2px solid @accent-blue;
      }

      &:hover:not(.active) {
        background-color: #3a3a3a;
        color: @text-primary;
      }
    }
  }

  // 审核提示条
  .notice-bar {
    background-color: #2c5e99;
    color: white;
    padding: 8px 15px;
    font-size: 13px;
    margin-bottom: 15px;
    border-radius: 4px;
  }

  // 筛选区域
  .filter-area {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 15px;
    flex-wrap: wrap;

    .filter-item {
      display: flex;
      align-items: center;
      gap: 8px;

      .filter-label {
        font-size: 14px;
        color: @text-secondary;
        min-width: 60px;
      }

      .filter-input {
        background-color: @input-bg;
        border: 1px solid @border-color;
        color: @text-primary;
        padding: 6px 10px;
        border-radius: 4px;
        font-size: 13px;
        min-width: 120px;

        &::placeholder {
          color: @text-secondary;
        }

        &:focus {
          outline: none;
          border-color: @accent-blue;
        }
      }

      .filter-separator {
        color: @text-secondary;
        margin: 0 5px;
      }
    }

    .filter-buttons {
      display: flex;
      gap: 8px;

      .filter-btn {
        background-color: @bg-secondary;
        border: 1px solid @border-color;
        color: @text-primary;
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 13px;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          border-color: @accent-blue;
          color: @accent-blue;
        }
      }
    }

    // 批量操作按钮样式
    .batch-operation {
      margin-left: auto;
      display: flex;
      gap: 10px;

      .batch-btn {
        padding: 6px 15px;
        border-radius: 4px;
        font-size: 13px;
        cursor: pointer;
        transition: all 0.2s;
        border: 1px solid @border-color;

        &.batch-select-all {
          background-color: @bg-secondary;
          color: @text-primary;

          &:hover {
            border-color: @accent-blue;
            color: @accent-blue;
          }
        }

        &.batch-price-btn {
          background-color: @accent-blue;
          color: #fff;
          border-color: @accent-blue;

          &:hover:not(:disabled) {
            background-color: #0284c7;
            border-color: #0284c7;
          }

          &:disabled {
            background-color: #4a4a4a;
            border-color: #4a4a4a;
            color: #999;
            cursor: not-allowed;
            opacity: 0.8;
          }
        }
      }
    }
  }

  // 模型列表
  .model-list {
    .model-table {
      width: max-content; // 表格宽度自适应内容，配合外层横向滚动
      border-collapse: collapse;

      th {
        background-color: @bg-secondary;
        color: @text-primary;
        font-weight: 500;
        padding: 12px 10px;
        text-align: left;
        border: 1px solid @border-color;
        font-size: 14px;
        white-space: nowrap;

        &.checkbox-column {
          width: 60px;
          text-align: center;
        }
      }

      td {
        padding: 12px 10px;
        border: 1px solid @border-color;
        font-size: 13px;
        color: @text-primary;
        vertical-align: middle;
        min-width: 100px;
        word-break: break-all;

        &.checkbox-cell {
          width: 60px;
          text-align: center;
        }
      }

      tr:nth-child(even) {
        background-color: #2a2a2a;
      }

      tr:hover {
        background-color: @hover-bg;
      }

      .model-thumbnail {
        .model-img {
          width: 80px;
          height: 60px;
          object-fit: cover;
          border-radius: 4px;
          cursor: pointer;
          transition: transform 0.2s;

          &:hover {
            transform: scale(1.05);
            box-shadow: 0 0 8px rgba(14, 165, 233, 0.5);
          }
        }
      }

      .model-title-cell {
        min-width: 280px;
        .model-title {
          cursor: pointer;
          transition: color 0.2s;
          &:hover {
            color: @accent-blue;
            text-decoration: underline;
          }
        }
      }

      .price {
        color: @accent-blue;
        font-weight: 500;
      }

      .guide-price {
        color: #93c5fd;
        font-weight: 400;
      }

      .apply-price-btn {
        background-color: #165dff;
        color: #fff;
        border-color: #165dff;
        &:hover {
          background-color: #0d47a1;
          border-color: #0d47a1;
          color: #fff;
        }
        &:disabled {
          background-color: #4a4a4a;
          border-color: #4a4a4a;
          color: #999;
          cursor: not-allowed;
          opacity: 0.8;
        }
      }

      .select-all-checkbox,
      .model-checkbox {
        width: 18px;
        height: 18px;
        cursor: pointer;
        accent-color: @accent-blue;
      }
    }
  }

  // 分页组件样式
  .pagination-container {
    margin-top: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0;
  }

  :deep(.el-pagination) {
    background-color: transparent !important;
    .el-pagination__total {
      color: #e0e0e0 !important;
      font-size: 14px !important;
    }
    .el-pager {
      li {
        background-color: #080a12 !important;
        color: #a0a0a0 !important;
        border: 1px solid #424242 !important;
        margin: 0 2px !important;
        &:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
          color: #0ea5e9 !important;
          border-color: #0ea5e9 !important;
        }
        &.active {
          background-color: #0ea5e9 !important;
          color: #ffffff !important;
          border-color: #0ea5e9 !important;
        }
      }
    }
    .btn-prev,
    .btn-next {
      background-color: @bg-secondary !important;
      color: @text-secondary !important;
      border: 1px solid @border-color !important;
      min-width: 30px !important;
      margin: 0 2px !important;
      &.disabled {
        background-color: darken(@bg-secondary, 5%) !important;
        color: darken(@text-secondary, 20%) !important;
        border-color: darken(@border-color, 10%) !important;
        cursor: not-allowed !important;
        &:hover {
          background-color: darken(@bg-secondary, 5%) !important;
          color: darken(@text-secondary, 20%) !important;
          border-color: darken(@border-color, 10%) !important;
        }
      }
      &:not(.disabled):hover {
        background-color: @hover-bg !important;
        color: @accent-blue !important;
        border-color: @accent-blue !important;
      }
      .el-icon {
        font-size: 12px !important;
        font-weight: bold !important;
      }
    }
    .el-pagination__jump {
      color: #a0a0a0 !important;
      .el-input__inner {
        background-color: #080a12 !important;
        color: #e0e0e0 !important;
        border: 1px solid #424242 !important;
        &:focus {
          border-color: #0ea5e9 !important;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .model-management-page {
      padding: 10px;
      .pagination-container {
        :deep(.el-pagination) {
          justify-content: center;
          flex-wrap: wrap;
          .btn-prev, .btn-next { margin: 2px !important; }
          .el-pager li { margin: 2px !important; }
        }
      }
      .filter-area {
        .batch-operation {
          margin-left: 0;
          margin-top: 10px;
          width: 100%;
          justify-content: flex-start;
        }
      }
    }
  }

  .operate-btn {
    background-color: @bg-secondary;
    border: 1px solid @border-color;
    color: @text-primary;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
    margin: 0 3px;
    &:hover {
      border-color: @accent-blue;
      color: @accent-blue;
    }
  }

  // 批量改价弹窗样式
  .price-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1001;

    .price-modal-content {
      background-color: @bg-secondary;
      border-radius: 8px;
      width: 400px;
      max-width: 90%;
      padding: 0;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);

      .price-modal-header {
        padding: 15px 20px;
        border-bottom: 1px solid @border-color;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .price-modal-title {
          font-size: 18px;
          color: @text-primary;
          margin: 0;
          font-weight: 500;
        }

        .price-modal-close {
          font-size: 24px;
          color: @text-secondary;
          cursor: pointer;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10%;
          transition: all 0.2s;
          &:hover {
            background-color: @hover-bg;
            color: @accent-blue;
          }
        }
      }

      .price-modal-body {
        padding: 20px;
        .price-input-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 15px;
          .price-label {
            font-size: 14px;
            color: @text-primary;
            font-weight: 500;
          }
          .price-input {
            background-color: @input-bg;
            border: 1px solid @border-color;
            color: @text-primary;
            padding: 10px;
            border-radius: 4px;
            font-size: 14px;
            &:focus {
              outline: none;
              border-color: @accent-blue;
            }
            &::placeholder {
              color: @text-secondary;
            }
          }
        }
        .selected-count {
          font-size: 13px;
          color: @text-secondary;
          margin-bottom: 10px;
        }
      }

      .price-modal-footer {
        padding: 15px 20px;
        border-top: 1px solid @border-color;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        .modal-btn {
          padding: 8px 20px;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
          &.cancel-btn {
            background-color: @bg-secondary;
            color: @text-primary;
            border: 1px solid @border-color;
            &:hover {
              border-color: @accent-blue;
              color: @accent-blue;
            }
          }
          &.confirm-btn {
            background-color: @accent-blue;
            color: #fff;
            border: 1px solid @accent-blue;
            &:hover:not(:disabled) {
              background-color: #0284c7;
              border-color: #0284c7;
            }
            &:disabled {
              background-color: #4a4a4a;
              border-color: #4a4a4a;
              color: #999;
              cursor: not-allowed;
              opacity: 0.8;
            }
          }
        }
      }
    }
  }
}
</style>